
// Shared logic for the Headlight logo endpoints (bespoke, NOT Meadow auto-REST). One controller drives
// a set of logo "slots" for a single owner id (a customer id, or a project id):
//   POST   <prefix><Base>/<id>          raw PNG bytes, Content-Type: image/png   -> {Success:true}
//   GET    <prefix><Base>/<id>          streams the PNG (200), transparent/none otherwise
//   GET    <prefix><Base>/Remove/<id>
// where <Base> is e.g. 'Customer/Logo', 'Customer/SecondaryLogo', 'Project/PrimaryLogo',
// 'Project/SecondaryLogo'. Both My Headlight (customer logos) and the Project workspace (project logos)
// use this so all four logos behave identically. Permission is owner-specific and computed by the host
// (role >= 5, or role 4 editing their own customer / a project in their customer); the host passes it in.

class LogoSlotController
{
	/**
	 * @param {any} pPict
	 * @param {String} pProviderName - the host provider's identifier (the slot template calls it back).
	 * @param {Array<{Key:String, Label:String, Help:String, Base:String}>} pSlotDefs
	 */
	constructor(pPict, pProviderName, pSlotDefs)
	{
		this.pict = pPict;
		this.providerName = pProviderName;
		this.slotDefs = pSlotDefs;

		this.ownerID = null;
		this.canManage = false;
		this._onChange = () => {};

		// Per-slot UI state.
		this.state = {};
		this.slotDefs.forEach((pDef) => { this.state[pDef.Key] = { hasLogo: false, cacheBust: 1, uploading: false }; });
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get _prefix()
	{
		return (this.restClient && this.restClient.options && this.restClient.options.ServerURL) || '/1.0/';
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	_toast(pMessage, pType)
	{
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); }
	}

	_def(pKey)
	{
		return this.slotDefs.find((pDef) => pDef.Key === pKey);
	}

	_url(pDef, pState)
	{
		return `${this._prefix}${pDef.Base}/${this.ownerID}?cb=${pState.cacheBust}`;
	}

	/**
	 * Point the controller at an owner (customer/project id) + permission, and register the host's
	 * re-render. Resets slot state so a fresh owner re-probes.
	 * @param {Number|String} pOwnerID
	 * @param {Boolean} pCanManage
	 * @param {Function} pOnChange
	 */
	setContext(pOwnerID, pCanManage, pOnChange)
	{
		this.ownerID = pOwnerID;
		this.canManage = !!pCanManage;
		this._onChange = (typeof pOnChange === 'function') ? pOnChange : () => {};
		this.slotDefs.forEach((pDef) => { this.state[pDef.Key] = { hasLogo: false, cacheBust: 1, uploading: false }; });
	}

	/** Probe every slot's logo existence (200 + a non-trivial body; a placeholder is tiny / 202). */
	async probeAll()
	{
		if (this.ownerID == null) { return; }
		await Promise.all(this.slotDefs.map((pDef) => this._probe(pDef)));
	}

	async _probe(pDef)
	{
		const tmpState = this.state[pDef.Key];
		try
		{
			const tmpResponse = await fetch(this._url(pDef, tmpState), { credentials: 'include' });
			if (tmpResponse.status !== 200) { tmpState.hasLogo = false; return; }
			const tmpLength = Number(tmpResponse.headers.get('Content-Length') || 0);
			if (tmpLength > 0) { tmpState.hasLogo = tmpLength > 1000; return; }
			const tmpBlob = await tmpResponse.blob();
			tmpState.hasLogo = tmpBlob.size > 1000;
		}
		catch (pError)
		{
			tmpState.hasLogo = false;
		}
	}

	/**
	 * Upload the chosen file (raw PNG POST) for one slot.
	 * @param {String} pKey
	 * @param {HTMLInputElement} pInput
	 */
	async upload(pKey, pInput)
	{
		const tmpDef = this._def(pKey);
		const tmpState = this.state[pKey];
		const tmpFile = (pInput && pInput.files && pInput.files[0]) ? pInput.files[0] : null;
		if (!tmpDef || !tmpFile) { return; }
		if (tmpFile.type !== 'image/png')
		{
			this._toast('Logos must be PNG files.', 'error');
			pInput.value = '';
			return;
		}
		tmpState.uploading = true;
		this._onChange();
		try
		{
			const tmpResponse = await fetch(`${this._prefix}${tmpDef.Base}/${this.ownerID}`,
				{ method: 'POST', credentials: 'include', headers: { 'Content-Type': 'image/png' }, body: tmpFile });
			if (!tmpResponse.ok) { throw new Error(`server returned ${tmpResponse.status}`); }
			tmpState.hasLogo = true;
			tmpState.cacheBust = tmpState.cacheBust + 1;
			tmpState.uploading = false;
			this._toast(`${tmpDef.Label} updated.`, 'success');
			this._onChange();
		}
		catch (pError)
		{
			tmpState.uploading = false;
			this._toast(`Could not upload ${tmpDef.Label.toLowerCase()}: ${pError.message || pError}`, 'error');
			this._onChange();
		}
		pInput.value = '';
	}

	/** Remove one slot's logo (confirm first). */
	remove(pKey)
	{
		const tmpDef = this._def(pKey);
		const tmpState = this.state[pKey];
		if (!tmpDef || !tmpState.hasLogo) { return; }
		const fApply = async () =>
		{
			try
			{
				const tmpResponse = await fetch(`${this._prefix}${tmpDef.Base}/Remove/${this.ownerID}`, { credentials: 'include' });
				if (!tmpResponse.ok) { throw new Error(`server returned ${tmpResponse.status}`); }
				tmpState.hasLogo = false;
				tmpState.cacheBust = tmpState.cacheBust + 1;
				this._toast(`${tmpDef.Label} removed.`, 'success');
				this._onChange();
			}
			catch (pError)
			{
				this._toast(`Could not remove ${tmpDef.Label.toLowerCase()}: ${pError.message || pError}`, 'error');
			}
		};
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(`Remove the ${tmpDef.Label.toLowerCase()}? Headlight will fall back to the default until a new one is uploaded.`,
				{ title: `Remove ${tmpDef.Label.toLowerCase()}`, confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else { fApply(); }
	}

	/** Build the per-slot render records the shared Headlight-LogoSlot template iterates. */
	buildRecords()
	{
		return this.slotDefs.map((pDef) =>
		{
			const tmpState = this.state[pDef.Key];
			return (
				{
					ProviderName: this.providerName,
					Key: pDef.Key,
					Label: pDef.Label,
					Help: pDef.Help,
					LogoSlot: tmpState.hasLogo ? [ { LogoURL: this._url(pDef, tmpState) } ] : [],
					EmptySlot: tmpState.hasLogo ? [] : [ {} ],
					UploadSlot: (this.canManage && !tmpState.uploading) ? [ { UploadLabel: tmpState.hasLogo ? 'Replace' : 'Upload', ProviderName: this.providerName, Key: pDef.Key } ] : [],
					UploadingSlot: tmpState.uploading ? [ {} ] : [],
					RemoveSlot: (tmpState.hasLogo && this.canManage && !tmpState.uploading) ? [ { ProviderName: this.providerName, Key: pDef.Key } ] : [],
					ReadOnlySlot: this.canManage ? [] : [ {} ],
				});
		});
	}
}

module.exports = LogoSlotController;

// The shared CSS for a logo slot (a transparency-checkerboard frame + actions). Hosts add this to their
// view CSS.
module.exports.LOGO_SLOT_CSS = /*css*/`
	.logo-slot { display: flex; gap: 1.5rem; align-items: flex-start; flex-wrap: wrap; padding: 0.4rem 0; }
	.logo-slot + .logo-slot { border-top: 1px solid var(--theme-color-border-light, #e8ebf0); margin-top: 0.6rem; padding-top: 1.1rem; }
	.logo-frame { flex: 0 0 auto; width: 15rem; height: 8.25rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px;
		background:
			linear-gradient(45deg, var(--theme-color-background-tertiary, #f1f3f6) 25%, transparent 25%),
			linear-gradient(-45deg, var(--theme-color-background-tertiary, #f1f3f6) 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, var(--theme-color-background-tertiary, #f1f3f6) 75%),
			linear-gradient(-45deg, transparent 75%, var(--theme-color-background-tertiary, #f1f3f6) 75%);
		background-size: 16px 16px; background-position: 0 0, 0 8px, 8px -8px, -8px 0;
		display: flex; align-items: center; justify-content: center; overflow: hidden; }
	.logo-frame img { max-width: 100%; max-height: 100%; object-fit: contain; }
	.logo-empty { color: var(--theme-color-text-muted, #6b7686); font-size: 0.85rem; text-align: center; padding: 0 1rem; }
	.logo-empty .icon { font-size: 1.5rem; display: block; margin: 0 auto 0.3rem; }
	.logo-slot-body { flex: 1 1 16rem; min-width: 14rem; }
	.logo-slot-label { font-size: 0.95rem; font-weight: 600; color: var(--theme-color-text-primary, #1f2733); margin: 0 0 0.2rem; }
	.logo-slot-help { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); margin: 0 0 0.9rem; line-height: 1.5; }
	.logo-slot-actions { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
	.logo-upload-label { display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; }
	.logo-upload-label input { display: none; }
	.logo-readonly { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); font-style: italic; }
	.logo-spin { display: inline-block; width: 1.1rem; height: 1.1rem; border: 2px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: logo-spin 0.7s linear infinite; vertical-align: -0.15rem; margin-right: 0.4rem; }
	@keyframes logo-spin { to { transform: rotate(360deg); } }
`;

// The shared templates a host iterates with {~TS:Headlight-LogoSlot:<...Slots address>~}. Handlers call
// back into the host provider by name (Record.ProviderName) so one template serves every logo.
module.exports.LOGO_SLOT_TEMPLATES =
[
	{
		Hash: 'Headlight-LogoSlot',
		Template: /*html*/`
			<div class="logo-slot">
				<div class="logo-frame">
					{~TS:Headlight-LogoSlot-Img:Record.LogoSlot~}
					{~TS:Headlight-LogoSlot-Empty:Record.EmptySlot~}
				</div>
				<div class="logo-slot-body">
					<p class="logo-slot-label">{~D:Record.Label~}</p>
					<p class="logo-slot-help">{~D:Record.Help~}</p>
					<div class="logo-slot-actions">
						{~TS:Headlight-LogoSlot-Upload:Record.UploadSlot~}
						{~TS:Headlight-LogoSlot-Uploading:Record.UploadingSlot~}
						{~TS:Headlight-LogoSlot-Remove:Record.RemoveSlot~}
						{~TS:Headlight-LogoSlot-ReadOnly:Record.ReadOnlySlot~}
					</div>
				</div>
			</div>`
	},
	{
		Hash: 'Headlight-LogoSlot-Img',
		Template: /*html*/`<img src="{~D:Record.LogoURL~}" alt="Logo" />`
	},
	{
		Hash: 'Headlight-LogoSlot-Empty',
		Template: /*html*/`<div class="logo-empty"><span class="icon">{~I:Image~}</span>None uploaded yet</div>`
	},
	{
		Hash: 'Headlight-LogoSlot-Upload',
		Template: /*html*/`<label class="button is-link logo-upload-label"><span class="icon">{~I:Upload~}</span><span>{~D:Record.UploadLabel~}</span><input type="file" accept="image/png" onchange="_Pict.providers['{~D:Record.ProviderName~}'].uploadSlot('{~D:Record.Key~}', this)" /></label>`
	},
	{
		Hash: 'Headlight-LogoSlot-Uploading',
		Template: /*html*/`<span class="logo-readonly"><span class="logo-spin"></span>Uploading…</span>`
	},
	{
		Hash: 'Headlight-LogoSlot-Remove',
		Template: /*html*/`<button class="button is-light" onclick="_Pict.providers['{~D:Record.ProviderName~}'].removeSlot('{~D:Record.Key~}')"><span class="icon">{~I:Trash~}</span><span>Remove</span></button>`
	},
	{
		Hash: 'Headlight-LogoSlot-ReadOnly',
		Template: /*html*/`<span class="logo-readonly">You don’t have permission to change this logo.</span>`
	}
];
